import React from "react"
import { Outlet, useParams, useLocation } from "react-router-dom"
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
} from "@chakra-ui/react"
import { ChevronRightIcon } from "@chakra-ui/icons"
import Sidebar from "./Sidebar"
import Link from "./Link"
import { Datasets, Schemas } from "../lib/store"
import Pixel from "./Pixel"
import SidebarFooter from "./SidebarFooter"
import breadcrumbs from "../lib/breadcrumbs"
import { generateBreadcrumbs } from "../lib/util"

const COLLAPSED_WIDTH = 60
const DEFAULT_WIDTH = 220
const MIN_WIDTH = 160
const MAX_WIDTH = 400

function useBreadcrumbs() {
  const location = useLocation()
  const params = useParams()
  const [datasets] = Datasets.useState()
  const [schemas] = Schemas.useState()

  return generateBreadcrumbs(location.pathname, breadcrumbs, [], params, {
    datasets,
    schemas,
  })
}

export default function SidebarLayout() {
  const crumbs = useBreadcrumbs()

  const [isCollapsed, setIsCollapsed] = React.useState(
    () => localStorage.getItem("sidebar-collapsed") === "true",
  )
  const [sidebarWidth, setSidebarWidth] = React.useState(
    () => parseInt(localStorage.getItem("sidebar-width")) || DEFAULT_WIDTH,
  )
  const [isResizing, setIsResizing] = React.useState(false)

  const effectiveWidth = isCollapsed ? COLLAPSED_WIDTH : sidebarWidth

  const handleToggleCollapse = React.useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev
      localStorage.setItem("sidebar-collapsed", String(next))
      return next
    })
  }, [])

  const handleResizeStart = React.useCallback((e) => {
    e.preventDefault()
    setIsResizing(true)

    const handleMouseMove = (e) => {
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, e.clientX))
      setSidebarWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }, [])

  React.useEffect(() => {
    if (!isResizing) {
      localStorage.setItem("sidebar-width", String(sidebarWidth))
    }
  }, [sidebarWidth, isResizing])

  return (
    <Box
      display="flex"
      flexDirection={{ base: "column", md: "row" }}
      height="100vh"
      maxW="100vw"
      overflow="hidden"
    >
      <Sidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        width={effectiveWidth}
        onResizeStart={handleResizeStart}
        isResizing={isResizing}
      />
      <Box
        maxWidth={{
          base: "100vw",
          md: `calc(100vw - ${effectiveWidth}px)`,
        }}
        minWidth={{
          base: "100vw",
          md: `calc(100vw - ${effectiveWidth}px)`,
        }}
        height={{ base: "calc(100vh - 4rem)", md: "100vh" }}
        bg="Gray.100"
        transition={
          isResizing ? "none" : "max-width 0.2s ease, min-width 0.2s ease"
        }
      >
        <Flex
          flexDirection="column"
          minWidth="100%"
          height="100%"
          alignItems="flex-start"
        >
          <Breadcrumb
            spacing={2}
            fontSize="xs"
            ml={[4, 4, 8]}
            pt={3}
            pb={1}
            separator={<ChevronRightIcon color="Gray.500" />}
          >
            {crumbs
              .filter((crumb) => crumb.label)
              .map((crumb) => (
                <BreadcrumbItem key={crumb.path} isCurrentPage={crumb.current}>
                  <BreadcrumbLink as={Link} to={crumb.path}>
                    {crumb.label}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              ))}
          </Breadcrumb>
          <Flex direction="column" flexGrow={1} minHeight={0} width="100%">
            <Outlet />
          </Flex>
          <SidebarFooter />
        </Flex>
      </Box>
      {process.env.NODE_ENV === "production" && <Pixel />}
    </Box>
  )
}
